/**
 * Utilities for safely handling, opening, and downloading attachments.
 * Prevents browser security errors when opening base64 data URLs in top frames:
 * "Not allowed to navigate top frame to data URL"
 */

/**
 * Converts a base64 data URL to a standard browser Blob object.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(",");
  const mimeMatch = parts[0]?.match(/:(.*?);/);
  const mime = mimeMatch && mimeMatch[1] ? mimeMatch[1] : "image/png";

  const base64Data = parts[1] || "";
  const byteString = atob(base64Data);
  const length = byteString.length;
  const uint8Array = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  return new Blob([uint8Array], { type: mime });
}

/**
 * Safely opens an attachment in a new tab.
 * If the attachment is a data: URL, converts it to an origin-bound Blob URL
 * so modern Chromium/Firefox browsers will not block top-frame navigation.
 */
export function openSafeAttachmentUrl(
  url: string,
  filename: string = "attachment",
): void {
  if (!url || typeof url !== "string") return;

  if (url.startsWith("data:")) {
    try {
      const blob = dataUrlToBlob(url);
      const blobUrl = URL.createObjectURL(blob);

      const newWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!newWindow) {
        // In case popup blocker intercepts, fallback to triggering download
        downloadAttachment(url, filename);
      }

      // Revoke the object URL after 60s to allow the new tab time to load
      setTimeout(() => {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch {
          // ignore revocation errors
        }
      }, 60000);
      return;
    } catch (err: unknown) {
      console.warn(
        "Failed to create blob URL from data URL, using fallback window write:",
        err,
      );
      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html lang="en">
            <head>
              <meta charset="utf-8" />
              <title>${filename}</title>
              <style>
                body {
                  margin: 0;
                  background-color: #0f172a;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                  color: #94a3b8;
                  font-family: sans-serif;
                }
                img {
                  max-width: 100%;
                  max-height: 100vh;
                  object-fit: contain;
                  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);
                }
              </style>
            </head>
            <body>
              <img src="${url}" alt="${filename}" />
            </body>
          </html>
        `);
        newWindow.document.close();
      }
      return;
    }
  }

  // Regular Cloud Storage / HTTP / HTTPS URL
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Downloads an attachment to the user's computer with the specified filename.
 */
export function downloadAttachment(
  url: string,
  filename: string = "attachment",
): void {
  if (!url || typeof url !== "string") return;

  // Ensure file has an extension
  let safeFilename = filename;
  if (!safeFilename.includes(".")) {
    if (url.startsWith("data:image/png")) safeFilename += ".png";
    else if (
      url.startsWith("data:image/jpeg") ||
      url.startsWith("data:image/jpg")
    )
      safeFilename += ".jpg";
    else if (url.startsWith("data:image/webp")) safeFilename += ".webp";
    else safeFilename += ".png";
  }

  if (url.startsWith("data:")) {
    try {
      const blob = dataUrlToBlob(url);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = safeFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return;
    } catch {
      // Fallback
      const a = document.createElement("a");
      a.href = url;
      a.download = safeFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
  }

  // For remote URLs: attempt fetch to download as blob, or fallback to anchor
  fetch(url)
    .then((res) => res.blob())
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = safeFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    })
    .catch(() => {
      // If CORS prevents fetch, fallback to window.open or direct anchor
      const a = document.createElement("a");
      a.href = url;
      a.download = safeFilename;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
}
